import React, {
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons";
import Avatar from "./Avatar";
import TinyLoader from "../loaders/TinyLoader";
import AppRoutes from "../../routes/AppRoutes";
import UserContext from "../../state/UserContext";
import { adminHelper } from "../../helpers/adminHelper";
import permissionHelper from "../../helpers/permissionHelper";
import userApi from "../../api/user/userApi";
import { UserProfileHoverDetails } from "../../types/dtos/users/UserProfileHoverDetails";

export type { UserProfileHoverDetails };

interface UserProfileHoverCardProps {
  /** The employee/user id to load profile details for */
  employeeId: string;
  /** Trigger element - the content that will show the hover card on hover */
  children: React.ReactNode;
  /** Optional class name for the wrapper around children */
  className?: string;
}

const HOVER_DELAY_MS = 600;
const CLOSE_DELAY_MS = 250;

/**
 * Portaled hover card for user profile details and a "View Profile" link.
 *
 * **When to use:** Default choice on normal page content — tables, analytics grids,
 * lists, and anywhere outside a modal. The card portals to `#root` and opens to the
 * right of the trigger.
 *
 * **When not to use:** Inside Radix modals/dialogs (`ModalPopup`, etc.) — pointer
 * events are trapped by the dialog and the card will close when the cursor moves
 * onto it. Use {@link UserProfileInlineHoverCard} instead.
 *
 * Hover behaviour is only enabled when the user would see the Users item in the
 * admin left nav (same logic as `AdminSubNavItems` for User Management).
 */
function UserProfileHoverCard({
  employeeId,
  children,
  className = "",
}: UserProfileHoverCardProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const api = new userApi(auth.user?.access_token);
  const userContext = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfileHoverDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Used only to ignore stale responses if the card closes or employeeId changes before the request completes */
  const fetchIdRef = useRef<string | null>(null);

  // Same condition as AdminSubNavItems for the 'Users' item (LeftNav)
  const hasUserJourneyModuleAccess = adminHelper.doesUserHaveAdminModuleAccess(
    userContext,
    "USER-JOURNEY",
  );
  const managerAdminsTheirPeopleConfig =
    userContext.user.client.moduleConfigs.find(
      (config) => config.key === "ManagersAdministerTheirEmployees",
    );
  const isNonAdminUserWithManagerConfigAccess =
    !userContext.user.isAdmin &&
    managerAdminsTheirPeopleConfig != null &&
    managerAdminsTheirPeopleConfig.value === "true" &&
    userContext.user.isManager;
  const hasUserManagementPermission =
    permissionHelper.userHasEitherPermission(
      userContext,
      "ViewAllEmployees",
      "MANAGE-EMPLOYEES",
    );
  const hasAccess =
    (hasUserJourneyModuleAccess && hasUserManagementPermission) ||
    isNonAdminUserWithManagerConfigAccess;

  const clearShowTimeout = useCallback(() => {
    if (showTimeoutRef.current != null) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
  }, []);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current != null) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearHideTimeout();
    if (!hasAccess || !employeeId) return;
    showTimeoutRef.current = setTimeout(() => setOpen(true), HOVER_DELAY_MS);
  }, [hasAccess, employeeId, clearHideTimeout]);

  const handleMouseLeave = useCallback(() => {
    clearShowTimeout();
    hideTimeoutRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [clearShowTimeout]);

  // Fetch profile when card opens; always fetch fresh (no cache). Only one in-flight request at a time.
  // Note: loading is intentionally omitted from deps so that when we setLoading(false) on completion we don't re-run this effect and start another request.
  useEffect(() => {
    if (!open || !employeeId) return;
    if (loading) return;

    setProfile(null);
    setError(false);
    setLoading(true);
    const thisFetchId = employeeId;
    fetchIdRef.current = thisFetchId;

    api.getProfileHoverDetails(
      employeeId,
      (data) => {
        if (fetchIdRef.current === thisFetchId) setProfile(data);
        setLoading(false);
      },
      () => {
        if (fetchIdRef.current === thisFetchId) setError(true);
        setLoading(false);
      },
    );
  }, [open, employeeId]);

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      clearShowTimeout();
      clearHideTimeout();
    },
    [clearShowTimeout, clearHideTimeout],
  );

  const [cardRoot] = useState(() =>
    typeof document !== "undefined" ? document.getElementById("root") : null,
  );
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!open || !triggerRef.current || !cardRoot) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCardPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  }, [open, cardRoot]);

  if (!hasAccess) {
    return <>{children}</>;
  }

  const cardContent = (
    <div
      ref={cardRef}
      className="rounded-lg shadow-lg border border-gray-200 bg-white p-4 min-w-[240px] max-w-[280px]"
      onMouseEnter={clearHideTimeout}
      onMouseLeave={handleMouseLeave}
    >
      {loading && !profile && (
        <div className="flex flex-col items-center justify-center gap-2 py-6 text-gray-600">
          <TinyLoader size="lg" />
          <span className="text-sm">Loading details...</span>
        </div>
      )}
      {error && !profile && (
        <div className="text-sm text-gray-600 py-2">
          Unable to load profile.
        </div>
      )}
      {profile && (
        <>
          <div className="flex gap-3">
            <Avatar
              imageUrl={profile.profilePicUrl}
              userFullName={profile.fullName}
              userInitials={profile.initials}
              size={12}
              activeStatus="DO-NOT-SHOW"
            />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-gray-900 truncate">
                {profile.fullName}
              </div>
              <div className="text-sm text-gray-600 truncate">
                {t(profile.jobTitleTranslationKeyIdentifier)}
              </div>
              {profile.locationName && (
                <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-600">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="text-gray-400 w-3.5 h-3.5 flex-shrink-0"
                  />
                  <span className="truncate">{profile.locationName}</span>
                </div>
              )}
              <div className="mt-2">
                <a
                  href={AppRoutes.admin.userManagement.userDetail.generateRoute(
                    employeeId,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary btn-sm inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white rounded-md"
                >
                  View Profile
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <span
      ref={triggerRef}
      className={`inline ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {open &&
        cardRoot &&
        createPortal(
          <div
            className="fixed z-[9999]"
            style={{
              top: cardPosition.top,
              left: cardPosition.left,
              transform: "translateY(-50%)",
            }}
          >
            {cardContent}
          </div>,
          cardRoot,
        )}
    </span>
  );
}

export default UserProfileHoverCard;
